/**
 * Page transitions, in the shape iOS uses.
 *
 * Going deeper slides in from the right; coming back slides in from the left. That pairing is
 * what makes a stack of screens feel like a stack rather than a series of unrelated pages — the
 * direction tells you which way you moved before you have read a word of the new screen.
 *
 * **The direction comes from the navigation type, not from the path.** React Router reports POP
 * for a back button and PUSH for a link, which is exactly the distinction iOS draws, and it is
 * right even when the two screens are unrelated. Guessing from path depth gets "deeper" wrong the
 * moment somebody navigates sideways.
 *
 * The easing is `cubic-bezier(.32,.72,0,1)` — the one Apple's own sheets use. It leaves fast and
 * lands slowly, which is what stops a 320ms move looking like a jump.
 *
 * The animation is remounted by keying on the location, so it replays on every navigation rather
 * than only the first. `prefers-reduced-motion` disables it in the stylesheet.
 */

import type { ReactNode } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

export function PageTransition({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigationType = useNavigationType();

  /**
   * The entry the app opened on, identified by React Router's own key for it.
   *
   * Every attempt to remember this in a ref or a module flag failed for the same reason:
   * StrictMode mounts each component twice in development, so anything meaning "have I rendered
   * before" is already true by the second render and the cold load slid after all. The history
   * key does not care how many times it is read.
   *
   * The trade-off, stated plainly: going *back* to the very first entry fades rather than
   * sliding, because that entry keeps its key for the life of the session. Landing where you
   * started and having it fade in is a small oddity; a page that slides in from the left the
   * moment you open the app is a wrong one.
   */
  const isFirst = location.key === "default";

  // A cold load also reports POP, and sliding in from the left on arrival reads as though the
  // user went back to a page they had never visited. The first entry fades instead.
  const cls = isFirst ? "page-enter" : navigationType === "POP" ? "page-pop" : "page-push";

  /**
   * Keyed on the path alone, **never on the query string**.
   *
   * Including the search string remounted the whole page every time a filter changed — and since
   * the search box writes `?q=` as you type, the input was destroyed and rebuilt after the first
   * character. Focus went with it, and every keystroke after the first was swallowed. The search
   * looked broken because it *was* broken, by the transition.
   *
   * It is also the right rule on its own terms: paging a table or narrowing a search is not
   * arriving at a new screen, and animating it would be motion for its own sake.
   */
  return (
    <div key={location.pathname} className={cls}>
      {children}
    </div>
  );
}
